<template>
  <div class="page-container billing-batch-settings">
    <portal to="page-name">Lotes</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <h1 class="card-title">
          <router-link to="/financial/billing-batches" class="card-title-back tooltip tooltip-right"
                       data-tooltip="Voltar para Lotes">
            <fa-icon :icon="['fal', 'arrow-left']"/>
          </router-link>
          Faturamento de convênios
        </h1>
      </div>
      <div class="card-body" v-if="loading">
        <div class="loading loading-lg"></div>
      </div>
      <div class="card-body settings-body" v-else>
        <div class="stenci-group stenci-alert" v-if="!cashRegisterEnabled">
          <p>
            <b>O faturamento de convênios exige o caixa novo ligado.</b>
            O particular é recebido pelo caixa e só as guias de convênio entram em lote.
            Ligue o caixa novo em Caixa › Configurações antes de ativar este módulo.
          </p>
        </div>
        <div class="settings-enable">
          <setting-item
            class="first-item"
            :key="switchKey"
            name="Usar o novo faturamento de convênios nesta filial"
            description="Guias por atendimento, lotes com número TISS, título a receber e repasse
              por profissional nascem no fechamento do lote. As guias e faturas antigas ficam em
              'Guias (legado)' e 'Faturas (legado)', somente leitura."
            type="switch"
            :disabled="saving || !cashRegisterEnabled"
            v-model="form.enabled"
          />
        </div>

        <template v-if="form.enabled">
          <div class="settings-section">Título a receber</div>
          <setting-item
            class="first-item"
            name="Categoria de receita do lote"
            description="Categoria do título a receber criado para cada lote fechado.">
            <div class="form-group" :class="{'has-error': $v.form.receivableCategoryId.$error}">
              <div class="input-group">
              <select class="form-select" v-model="form.receivableCategoryId"
                      @blur="$v.form.receivableCategoryId.$touch()">
                <option v-for="option in revenueCategoryOptions" :value="option.value"
                        :key="String(option.value)">{{ option.text }}</option>
              </select>
              <button class="btn btn-neutral btn-icon btn-action input-group-btn
                             tooltip tooltip-left"
                      data-tooltip="Nova categoria de receita" :disabled="saving"
                      @click="openCategoryModal('revenue', 'receivableCategoryId')">
                <fa-icon :icon="['fal', 'plus']"/>
              </button>
              </div>
              <div class="form-input-hint" v-if="$v.form.receivableCategoryId.$error">
                Campo obrigatório
              </div>
            </div>
          </setting-item>
          <setting-item
            name="Categoria de recuperação de glosa"
            description="Receita complementar gerada quando a operadora acata um recurso.
              Se ficar em branco, usa a categoria do lote.">
            <div class="form-group">
              <div class="input-group">
              <select class="form-select" v-model="form.recoveryCategoryId">
                <option v-for="option in revenueCategoryOptions" :value="option.value"
                        :key="String(option.value)">{{ option.text }}</option>
              </select>
              <button class="btn btn-neutral btn-icon btn-action input-group-btn
                             tooltip tooltip-left"
                      data-tooltip="Nova categoria de receita" :disabled="saving"
                      @click="openCategoryModal('revenue', 'recoveryCategoryId')">
                <fa-icon :icon="['fal', 'plus']"/>
              </button>
              </div>
            </div>
          </setting-item>
          <setting-item
            name="Previsão de recebimento"
            description="Dias após o fechamento do lote para o vencimento do título.
              Cada plano pode ter o próprio prazo no cadastro do convênio.">
            <div class="form-group" :class="{'has-error': $v.form.defaultPaymentDays.$error}">
              <div class="input-group">
                <dx-input-number class="form-input text-right" maxlength="3"
                                 v-model="form.defaultPaymentDays"
                                 @blur="$v.form.defaultPaymentDays.$touch()"/>
                <span class="input-group-addon">dias</span>
              </div>
              <div class="form-input-hint" v-if="$v.form.defaultPaymentDays.$error">
                Informe entre 1 e 365 dias
              </div>
            </div>
          </setting-item>

          <div class="settings-section">Repasse dos profissionais</div>
          <setting-item
            class="first-item"
            name="Categoria de despesa do repasse"
            description="Categoria da despesa criada por profissional em cada lote.">
            <div class="form-group" :class="{'has-error': $v.form.transferCategoryId.$error}">
              <div class="input-group">
              <select class="form-select" v-model="form.transferCategoryId"
                      @blur="$v.form.transferCategoryId.$touch()">
                <option v-for="option in expenseCategoryOptions" :value="option.value"
                        :key="String(option.value)">{{ option.text }}</option>
              </select>
              <button class="btn btn-neutral btn-icon btn-action input-group-btn
                             tooltip tooltip-left"
                      data-tooltip="Nova categoria de despesa" :disabled="saving"
                      @click="openCategoryModal('expense', 'transferCategoryId')">
                <fa-icon :icon="['fal', 'plus']"/>
              </button>
              </div>
              <div class="form-input-hint" v-if="$v.form.transferCategoryId.$error">
                Campo obrigatório
              </div>
            </div>
          </setting-item>
          <setting-item
            name="Forma de pagamento do repasse"
            description="Vem preenchida ao pagar o repasse. O operador pode trocar na hora.">
            <div class="form-group" :class="{'has-error': $v.form.transferMethodId.$error}">
              <select class="form-select" v-model="form.transferMethodId"
                      @blur="$v.form.transferMethodId.$touch()">
                <option v-for="option in methodOptions" :value="option.value"
                        :key="String(option.value)">{{ option.text }}</option>
              </select>
              <div class="form-input-hint" v-if="$v.form.transferMethodId.$error">
                Campo obrigatório
              </div>
            </div>
          </setting-item>
          <setting-item
            name="Conta financeira do repasse"
            description="Conta de onde sai o pagamento dos repasses.">
            <div class="form-group" :class="{'has-error': $v.form.transferBankAccountId.$error}">
              <div class="input-group">
              <select class="form-select" v-model="form.transferBankAccountId"
                      @blur="$v.form.transferBankAccountId.$touch()">
                <option v-for="option in bankAccountOptions" :value="option.value"
                        :key="String(option.value)">{{ option.text }}</option>
              </select>
              <button class="btn btn-neutral btn-icon btn-action input-group-btn
                             tooltip tooltip-left"
                      data-tooltip="Nova conta interna" :disabled="saving"
                      @click="openBankAccountModal('transferBankAccountId')">
                <fa-icon :icon="['fal', 'plus']"/>
              </button>
              </div>
              <div class="form-input-hint" v-if="$v.form.transferBankAccountId.$error">
                Campo obrigatório
              </div>
            </div>
          </setting-item>
          <setting-item
            name="Pagamento do repasse"
            :description="transferPaymentDescription">
            <div class="form-group">
              <select class="form-select" v-model="form.transferPayment">
                <option v-for="option in transferPaymentOptions" :value="option.value"
                        :key="option.value">{{ option.text }}</option>
              </select>
            </div>
          </setting-item>
          <setting-item
            name="Centro de custo"
            description="Opcional. Aplicado ao título e aos repasses de cada lote.">
            <div class="form-group">
              <select class="form-select" v-model="form.costCenterId">
                <option v-for="option in costCenterOptions" :value="option.value"
                        :key="String(option.value)">{{ option.text }}</option>
              </select>
            </div>
          </setting-item>
        </template>
      </div>
      <bank-account-modal
        v-if="bankAccountModal.show"
        :show="bankAccountModal.show"
        @saved="bankAccountSaved"
        @close="bankAccountModal.show = false"
      />
      <category-modal
        v-if="categoryModal.show"
        :show="categoryModal.show"
        :type="categoryModal.type"
        :categories="categories.filter(item => item.type === categoryModal.type)"
        @saved="categorySaved"
        @close="categoryModal.show = false"
      />
      <div class="card-footer" v-if="!loading">
        <button class="btn btn-primary mr-1" @click="save" v-if="form.enabled"
                :disabled="saving" :class="{loading: saving}">
          Salvar
        </button>
        <router-link to="/financial/billing-batches" class="btn">Voltar</router-link>
      </div>
    </div>
    <forbidden v-else></forbidden>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  requiredIf, minValue, maxValue,
} from 'vuelidate/lib/validators';
import { UPDATE_BRANCH } from '@/data/actions/modules/general';
import Forbidden from '@/components/auth/Forbidden.vue';
import { categoryOptions } from '@/helpers/category';
import SettingItem from '../../../setting/components/SettingItem.vue';
import CategoryModal from '../../../category/components/Modal.vue';
import BankAccountModal from '../../../cash-register/modals/BankAccountModal.vue';

const SELECT_PLACEHOLDER = { value: null, text: '[Selecione]' };
const OPTIONAL_PLACEHOLDER = { value: null, text: '[Não usar]' };
const DEFAULT_TRANSFER_PAYMENT = 'receipt';
const TRANSFER_PAYMENT_OPTIONS = [
  {
    value: 'receipt',
    text: 'No recebimento',
    description: 'O repasse é pago de uma vez: no lote, na previsão de pagamento; '
      + 'no caixa, mesmo que o paciente parcele.',
  },
  {
    value: 'flow',
    text: 'Acompanhando o fluxo',
    description: 'No caixa, se o paciente parcelou em 10x, o repasse é gerado em 10 parcelas '
      + 'com os mesmos vencimentos.',
  },
];

function toOption(item) {
  return { value: item.id, text: item.name };
}


function byType(type) {
  return item => item.type === type;
}

function blankForm() {
  return {
    enabled: false,
    receivableCategoryId: null,
    transferCategoryId: null,
    recoveryCategoryId: null,
    transferMethodId: null,
    transferBankAccountId: null,
    costCenterId: null,
    defaultPaymentDays: 30,
    transferPayment: DEFAULT_TRANSFER_PAYMENT,
  };
}

export default {
  components: {
    Forbidden,
    SettingItem,
    CategoryModal,
    BankAccountModal,
  },
  data() {
    return {
      loading: true,
      ready: false,
      silent: false,
      switchKey: 0,
      saving: false,
      cashRegisterEnabled: false,
      bankAccounts: [],
      bankAccountModal: { show: false, target: null },
      categoryModal: { show: false, type: null, target: null },
      categories: [],
      methods: [],
      costCenters: [],
      form: blankForm(),
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(UPDATE_BRANCH);
    },
    revenueCategoryOptions() {
      return [
        SELECT_PLACEHOLDER,
        ...categoryOptions(this.categories.filter(byType('revenue'))),
      ];
    },
    expenseCategoryOptions() {
      return [
        SELECT_PLACEHOLDER,
        ...categoryOptions(this.categories.filter(byType('expense'))),
      ];
    },
    bankAccountOptions() {
      return [SELECT_PLACEHOLDER, ...this.bankAccounts.map(toOption)];
    },
    methodOptions() {
      return [SELECT_PLACEHOLDER, ...this.methods.map(toOption)];
    },
    costCenterOptions() {
      return [OPTIONAL_PLACEHOLDER, ...this.costCenters.map(toOption)];
    },
    transferPaymentOptions() {
      return TRANSFER_PAYMENT_OPTIONS;
    },
    transferPaymentDescription() {
      const option = TRANSFER_PAYMENT_OPTIONS
        .find(item => item.value === this.form.transferPayment);
      return (option || TRANSFER_PAYMENT_OPTIONS[0]).description;
    },
  },
  validations() {
    const whenEnabled = requiredIf(() => this.form.enabled);
    return {
      form: {
        receivableCategoryId: { required: whenEnabled },
        transferCategoryId: { required: whenEnabled },
        transferMethodId: { required: whenEnabled },
        transferBankAccountId: { required: whenEnabled },
        defaultPaymentDays: {
          required: whenEnabled,
          minValue: minValue(1),
          maxValue: maxValue(365),
        },
      },
    };
  },
  async mounted() {
    if (!this.hasAccess) {
      this.loading = false;
      return;
    }
    try {
      const [
        { data: settings },
        { data: accounts },
        { data: categories },
        { data: methods },
        { data: costCenters },
      ] = await Promise.all([
        this.$http.get('/facility-settings'),
        this.$http.get('/bank-accounts', { params: { limit: 0 } }),
        this.$http.get('/categories', { params: { active: true, limit: 0 } }),
        this.$http.get('/payment-methods', { params: { active: true, limit: 0 } }),
        this.$http.get('/cost-centers', { params: { limit: 0 } }),
      ]);
      this.bankAccounts = accounts.items || [];
      this.categories = categories.items || [];
      this.methods = methods.items || [];
      this.costCenters = costCenters.items || [];
      this.cashRegisterEnabled = !!(settings.cashRegister && settings.cashRegister.enabled);
      const current = settings.insuranceBilling || {};
      Object.keys(this.form).forEach((key) => {
        if (current[key] !== undefined && current[key] !== null) {
          this.form[key] = current[key];
        }
      });
    } catch (e) {
      this.$toast.error(e);
    }
    this.loading = false;
    this.$nextTick(() => {
      this.ready = true;
    });
  },
  watch: {
    'form.enabled': function onEnabledChange(enabled) {
      if (!this.ready || this.silent || enabled) return;
      this.setEnabledSilently(true);
      this.confirmDisable();
    },
  },
  methods: {
    openBankAccountModal(target) {
      this.bankAccountModal = { show: true, target };
    },
    bankAccountSaved(account) {
      this.bankAccounts.push(account);
      this.form[this.bankAccountModal.target] = account.id;
      if (this.$v.form[this.bankAccountModal.target]) {
        this.$v.form[this.bankAccountModal.target].$touch();
      }
    },
    openCategoryModal(type, target) {
      this.categoryModal = { show: true, type, target };
    },
    categorySaved(category) {
      this.categories.push(category);
      this.form[this.categoryModal.target] = category.id;
      if (this.$v.form[this.categoryModal.target]) {
        this.$v.form[this.categoryModal.target].$touch();
      }
      this.categoryModal.show = false;
    },
    setEnabledSilently(value) {
      this.silent = true;
      this.form.enabled = value;
      this.switchKey += 1;
      this.$nextTick(() => {
        this.silent = false;
      });
    },
    confirmDisable() {
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Desativar o faturamento de convênios?</h5>'
          + '<div>O menu "Lotes" deixa de montar lotes e a tela de faturas volta ao modelo '
          + 'anterior. Lotes, títulos e repasses já gerados continuam no financeiro.</div>'
          + '</div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim, desativar',
            classes: 'btn-error ml-2',
            click: (close) => {
              this.setEnabledSilently(false);
              return this.save().finally(close);
            },
          },
        ],
      });
    },
    save() {
      this.$v.form.$touch();
      if (this.form.enabled && this.$v.form.$error) {
        this.$toast.error('Preencha os campos obrigatórios.');
        return Promise.resolve();
      }

      const insuranceBilling = {
        ...this.form,
        receivableCategoryId: this.form.receivableCategoryId || null,
        transferCategoryId: this.form.transferCategoryId || null,
        recoveryCategoryId: this.form.recoveryCategoryId || null,
        transferMethodId: this.form.transferMethodId || null,
        transferBankAccountId: this.form.transferBankAccountId || null,
        costCenterId: this.form.costCenterId || null,
        defaultPaymentDays: Number(this.form.defaultPaymentDays) || 30,
        transferPayment: this.form.transferPayment || DEFAULT_TRANSFER_PAYMENT,
      };

      this.saving = true;
      return this.$http.put('/facility-settings', { insuranceBilling })
        .then(() => this.$store.dispatch('loadUser'))
        .then(() => {
          this.$v.form.$reset();
          this.$toast.success(this.form.enabled
            ? 'Faturamento de convênios ligado. A tela Lotes já está disponível.'
            : 'Faturamento de convênios desligado.');
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../batch.scss";

.billing-batch-settings {
  .settings-body {
    max-width: 62rem;
  }
  .stenci-group {
    margin-bottom: $layout-spacing;
  }
  .settings-enable {
    background-color: #FFF8E6;
    border: #F2C94C solid $border-width;
    border-radius: $border-radius;
    padding: 0 $layout-spacing;
    .setting-item {
      border-top: 0;
    }
    .setting-item-name {
      font-size: $font-size-lg;
      font-weight: 600;
    }
  }
  .settings-section {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: .04em;
    margin: $layout-spacing-lg 0 $layout-spacing-sm;
    text-transform: uppercase;
  }
}
</style>
